import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import PropertiesList from '@/pages/PropertiesList';
import ViolationsTab from '@/pages/ViolationsTab';
import DotPermitsTab from '@/pages/DotPermitsTab';
import { DobFilings } from '@/components/DobFilings';
import FdnyPermits from '@/components/FdnyPermits';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'violations', label: 'Violations' },
  { value: 'dot-permits', label: 'DOT Permits' },
  { value: 'dob-filings', label: 'DOB Filings' },
  { value: 'fdny-permits', label: 'FDNY Permits' },
];

// Count badge next to a tab label. Scarlet when items need attention, neutral blue otherwise.
function TabBadge({ text, urgent, testid }) {
  const palette = urgent
    ? { backgroundColor: '#FEF2F2', color: '#E5441A' }
    : { backgroundColor: '#EFF6FF', color: '#1B4A76' };
  return (
    <span
      data-testid={testid}
      style={{ ...palette, borderRadius: '4px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 500, padding: '2px 6px', lineHeight: 1.3 }}
    >
      {text}
    </span>
  );
}

export default function PropertiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  // Back-compat: the Overview tab was previously named "properties".
  const normalized = raw === 'properties' ? 'overview' : raw;
  const tab = TABS.some(t => t.value === normalized) ? normalized : 'overview';

  const [counts, setCounts] = useState({ properties: null, openViolations: null, permitsExpiring: null, permitsActive: null, filingsExpiring: null, filingsTotal: null, fdnyExpiring: null, fdnyActive: null });

  useEffect(() => {
    api.get('/properties').then(r => setCounts(c => ({ ...c, properties: Array.isArray(r.data) ? r.data.length : 0 }))).catch(() => {});
    api.get('/violations/summary').then(r => setCounts(c => ({ ...c, openViolations: r.data?.total_open ?? 0 }))).catch(() => {});
    api.get('/dot-permits/summary').then(r => setCounts(c => ({ ...c, permitsExpiring: r.data?.expiring_30 ?? 0, permitsActive: r.data?.total_active ?? 0 }))).catch(() => {});
    api.get('/dob-filings/summary').then(r => setCounts(c => ({ ...c, filingsExpiring: r.data?.expiring_soon ?? 0, filingsTotal: r.data?.total_filings ?? 0 }))).catch(() => {});
    api.get('/fdny-permits/summary').then(r => setCounts(c => ({ ...c, fdnyExpiring: r.data?.expiring_30 ?? 0, fdnyActive: r.data?.total_active ?? 0 }))).catch(() => {});
  }, [tab]);

  const changeTab = (t) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('tab', t);
      p.delete('open');
      return p;
    });
  };

  const badgeFor = (value) => {
    if (value === 'overview') {
      if (counts.properties === null) return null;
      return <TabBadge text={`${counts.properties}`} urgent={false} testid="tab-badge-overview" />;
    }
    if (value === 'violations') {
      if (counts.openViolations === null) return null;
      return <TabBadge text={`${counts.openViolations}`} urgent={counts.openViolations > 0} testid="tab-badge-violations" />;
    }
    if (value === 'dot-permits') {
      if (counts.permitsExpiring === null) return null;
      if (counts.permitsExpiring > 0) return <TabBadge text={`${counts.permitsExpiring} expiring`} urgent testid="tab-badge-dot-permits" />;
      return <TabBadge text={`${counts.permitsActive ?? 0}`} urgent={false} testid="tab-badge-dot-permits" />;
    }
    if (value === 'dob-filings') {
      if (counts.filingsExpiring === null) return null;
      if (counts.filingsExpiring > 0) return <TabBadge text={`${counts.filingsExpiring} expiring`} urgent testid="tab-badge-dob-filings" />;
      return <TabBadge text={`${counts.filingsTotal ?? 0}`} urgent={false} testid="tab-badge-dob-filings" />;
    }
    if (value === 'fdny-permits') {
      if (counts.fdnyExpiring === null) return null;
      if (counts.fdnyExpiring > 0) return <TabBadge text={`${counts.fdnyExpiring} expiring`} urgent testid="tab-badge-fdny-permits" />;
      return <TabBadge text={`${counts.fdnyActive ?? 0}`} urgent={false} testid="tab-badge-fdny-permits" />;
    }
    return null;
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="properties-hub">
      <h1 className="font-semibold text-lg tracking-tight text-foreground">Properties</h1>
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0 h-auto flex-wrap" data-testid="properties-hub-tabs">
          {TABS.map(t => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-xs data-[state=active]:border-[#E5441A] data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:shadow-none"
              data-testid={`hub-tab-${t.value}`}
            >
              <span className="inline-flex items-center gap-2">
                {t.label}
                {badgeFor(t.value)}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-4"><PropertiesList /></TabsContent>
        <TabsContent value="violations" className="mt-4"><ViolationsTab /></TabsContent>
        <TabsContent value="dot-permits" className="mt-4"><DotPermitsTab /></TabsContent>
        <TabsContent value="dob-filings" className="mt-4"><DobFilings scope="all" /></TabsContent>
        <TabsContent value="fdny-permits" className="mt-4"><FdnyPermits /></TabsContent>
      </Tabs>
    </div>
  );
}
